import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { isNil } from "lodash";
import clsx from "clsx";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import {
  Dialog,
  DialogContent,
  Button,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Switch,
  Tab,
  Tabs,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import TabPanel from "../TabPanel";
import { Autorenew, FileCopy } from "@material-ui/icons";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import SchedulesForm from "../SchedulesForm";
import { getTokens, brandAlpha } from "../../styles/tokens";
import { usePanelStyles, ModalTitle, FormSection, SettingRow } from "../FormFields/panel";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const danger = t.mode === "dark" ? t.status.down.fg : t.semantic.error;
  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
      gap: 4
    },
    // altura estável: trocar de aba não faz o modal mudar de tamanho
    dialog: {
      height: "min(780px, calc(100% - 64px))",
      [theme.breakpoints.down("xs")]: { height: "calc(100% - 24px)" },
    },
    body: { ...theme.scrollbarStyles },
    // componentes prontos (QueueSelect) trazem margem própria; aqui quem espaça é a grade
    flush: { "& .MuiFormControl-root": { margin: 0 }, "& > div": { marginTop: "0 !important" } },
    spaced: { marginTop: 14 },
    // campo + ação ao lado (chave da API): a ação alinha pela base do campo
    inlineRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: 6,
      minWidth: 0,
      "& > .MuiFormControl-root": { flex: "1 1 auto", minWidth: 0, margin: 0 },
      "& .MuiIconButton-root": { flex: "none", width: 40, height: 40, padding: 0, color: t.textSecondary },
      "& .MuiIconButton-root:hover": { color: t.text },
    },
    copied: { color: `${t.accent} !important` },
    uploadRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      minWidth: 0,
      "& .MuiChip-root": { maxWidth: "100%" },
    },
    importAlert: {
      marginTop: 14,
      padding: "10px 12px",
      borderRadius: t.radius.sm,
      border: `1px solid ${brandAlpha(t.semantic.error, 0.3)}`,
      backgroundColor: brandAlpha(t.semantic.error, t.mode === "dark" ? 0.16 : 0.06),
      fontFamily: t.font,
      fontSize: 12.5,
      lineHeight: 1.55,
      color: danger,
    },

    btnWrapper: {
      position: "relative",
    },

    buttonProgress: {
      color: t.accent,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  };
});

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const p = usePanelStyles();
  const [autoToken, setAutoToken] = useState("");

  const inputFileRef = useRef(null);

  const [attachment, setAttachment] = useState(null)
  const [attachmentName, setAttachmentName] = useState('')

  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    token: "",
    maxUseBotQueues: 3,
    provider: "beta",
    expiresTicket: 0,
    allowGroup: false,
    groupAsTicket: "disabled",
    timeUseBotQueues: '0',
    timeSendQueue: '0',
    sendIdQueue: 0,
    expiresTicketNPS: '0',
    expiresInactiveMessage: "",
    timeInactiveMessage: "",
    inactiveMessage: "",
    maxUseBotQueuesNPS: 3,
    whenExpiresTicket: 0,
    timeCreateNewTicket: 0,
    greetingMediaAttachment: "",
    importRecentMessages: "",
    importOldMessages: "",
    importOldMessagesGroups: "",
    integrationId: "",
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queues, setQueues] = useState([]);
  const [tab, setTab] = useState("general");
  const [enableImportMessage, setEnableImportMessage] = useState(false);
  const [importOldMessagesGroups, setImportOldMessagesGroups] = useState(false);
  const [closedTicketsPostImported, setClosedTicketsPostImported] = useState(false);
  const [importOldMessages, setImportOldMessages] = useState(moment().add(-1, "days").format("YYYY-MM-DDTHH:mm"));
  const [importRecentMessages, setImportRecentMessages] = useState(moment().add(-1, "minutes").format("YYYY-MM-DDTHH:mm"));
  const [copied, setCopied] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);


  const [schedules, setSchedules] = useState([
    { weekday: i18n.t("queueModal.serviceHours.monday"), weekdayEn: "monday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.tuesday"), weekdayEn: "tuesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.wednesday"), weekdayEn: "wednesday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.thursday"), weekdayEn: "thursday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: i18n.t("queueModal.serviceHours.friday"), weekdayEn: "friday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: "Sábado", weekdayEn: "saturday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
    { weekday: "Domingo", weekdayEn: "sunday", startTimeA: "08:00", endTimeA: "12:00", startTimeB: "13:00", endTimeB: "18:00", },
  ]);

  const { get: getSetting } = useCompanySettings();
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  useEffect(() => {

    const fetchData = async () => {

      const setting = await getSetting({
        "column": "scheduleType"
      });
      setSchedulesEnabled(setting.scheduleType === "connection");
    }
    fetchData();
  }, []);

  const handleEnableImportMessage = async (e) => {
    setEnableImportMessage(e.target.checked);

  };

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp-admin/${whatsAppId}?session=0`);
        setWhatsApp(data);
        setAttachmentName(data.greetingMediaAttachment);
        setAutoToken(data.token);
        data.promptId ? setSelectedPrompt(data.promptId) : setSelectedPrompt(null);
        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
        setSchedules(data.schedules)
        if (data?.importOldMessages) {
          setEnableImportMessage(true);
          setImportOldMessages(data?.importOldMessages);
          setImportRecentMessages(data?.importRecentMessages);
          setClosedTicketsPostImported(data?.closedTicketsPostImported);
          setImportOldMessagesGroups(data?.importOldMessagesGroups);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queueIntegration");

        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
    setSelectedQueueIds([]);
  };

  const handleSaveWhatsApp = async (values) => {
    if (!whatsAppId) setAutoToken(generateRandomCode(30));
   
    if ((!isNil(values.ratingMessage) && values.ratingMessage !== '') && (values.expiresTicketNPS === '0' || values.expiresTicketNPS === '' || values.expiresTicketNPS === 0)) {
      toastError(i18n.t("whatsappModal.errorExpiresNPS"));
      return;
    }

    if (!isNil(values.ratingMessage) && (values.expiresTicketNPS === '0' || values.expiresTicketNPS === '')) {
      toastError(i18n.t("whatsappModal.errorExpiresNPS"));
      return;
    }
    if (values.timeSendQueue === '') values.timeSendQueue = '0'
    
    if  ((values.sendIdQueue === 0 || values.sendIdQueue === '' || isNil(values.sendIdQueue)) &&  (values.timeSendQueue !== 0 && values.timeSendQueue !== '0')) {
      toastError(i18n.t("whatsappModal.errorSendQueue"));
      return;
    }

    const whatsappData = {
      ...values, queueIds: selectedQueueIds,
      importOldMessages: enableImportMessage ? importOldMessages : null,
      importRecentMessages: enableImportMessage ? importRecentMessages : null,
      importOldMessagesGroups: importOldMessagesGroups ? importOldMessagesGroups : null,
      closedTicketsPostImported: closedTicketsPostImported ? closedTicketsPostImported : null,
      token: autoToken ? autoToken : null, schedules,
      promptId: selectedPrompt ? selectedPrompt : null
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];

    try {
      if (whatsAppId) {
        if (whatsAppId && enableImportMessage && whatsApp?.status === "CONNECTED") {
          toast.warning(
            i18n.t("userModal.warning.updateImage"),
            { autoClose: false }
          );
          try {
            setWhatsApp({ ...whatsApp, status: "qrcode" });
            await api.delete(`/whatsappsession/${whatsApp.id}`);
          } catch (err) {
            toastError(err);
          }
        }

        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/whatsapp/${whatsAppId}/media-upload`, formData);
        }
        if (!attachmentName && (whatsApp.greetingMediaAttachment !== null)) {
          await api.delete(`/whatsapp/${whatsAppId}/media-upload`);
        }
      } else {
        const { data } = await api.post("/whatsapp", whatsappData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/whatsapp/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("whatsappModal.success"));

      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  function generateRandomCode(length) {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyvz0123456789";
    let code = "";

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      code += charset.charAt(randomIndex);
    }
    return code;
  }

  const handleRefreshToken = () => {
    setAutoToken(generateRandomCode(30));
  }

  const handleCopyToken = () => {
    navigator.clipboard.writeText(autoToken); // Copia o token para a área de transferência    
    setCopied(true); // Define o estado de cópia como verdadeiro
  };

  const handleSaveSchedules = async (values) => {
    toast.success("Clique em salvar para registar as alterações");
    setSchedules(values);
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    // inputFileRef.current.value = null
    setAttachment(null)
    setAttachmentName("")
    setCopied(false);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleFileUpload = () => {
    const file = inputFileRef.current.files[0];
    setAttachment(file)
    setAttachmentName(file.name)
    inputFileRef.current.value = null
  };

  const handleDeleFile = () => {
    setAttachment(null)
    setAttachmentName(null)
  }

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        classes={{ paper: clsx(p.dialogPaper, classes.dialog) }}
        aria-labelledby="whatsapp-admin-title"
      >
        <ModalTitle
          id="whatsapp-admin-title"
          title={whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
          subtitle="Configuração da conexão pelo administrador da plataforma."
          onClose={handleClose}
        />
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting }) => (
            <Form className={p.modalForm}>
              <Tabs
                value={tab}
                scrollButtons="auto"
                variant="scrollable"
                onChange={handleTabChange}
                className={p.modalTabs}
              >
                <Tab label={i18n.t("whatsappModal.tabs.general")} value={"general"} />
                <Tab label={i18n.t("whatsappModal.tabs.integrations")} value={"integrations"} />
                <Tab label={i18n.t("whatsappModal.tabs.messages")} value={"messages"} />
                <Tab label="Chatbot" value={"chatbot"} />
                <Tab label={i18n.t("whatsappModal.tabs.assessments")} value={"nps"} />
                {schedulesEnabled && <Tab label={i18n.t("whatsappModal.tabs.schedules")} value={"schedules"} />}
              </Tabs>
              <DialogContent className={clsx(p.modalBody, p.labelsAbove, classes.body)}>
                <TabPanel
                  value={tab}
                  name={"general"}
                >
                  {/* NOME E PADRAO */}
                  <FormSection title="Identificação">
                    <div className={p.stack}>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.name")}
                        autoFocus
                        name="name"
                        id="connection-name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    </div>
                  </FormSection>

                  <FormSection title="Comportamento">
                    <SettingRow label={i18n.t("whatsappModal.form.default")}>
                      <Field
                        as={Switch}
                        color="primary"
                        name="isDefault"
                        checked={values.isDefault}
                        inputProps={{ "aria-label": i18n.t("whatsappModal.form.default") }}
                      />
                    </SettingRow>
                    <SettingRow label={i18n.t("whatsappModal.form.group")}>
                      <Field
                        as={Switch}
                        color="primary"
                        name="allowGroup"
                        checked={values.allowGroup}
                        inputProps={{ "aria-label": i18n.t("whatsappModal.form.group") }}
                      />
                    </SettingRow>
                    <SettingRow wide label={i18n.t("whatsappModal.form.groupAsTicket")}>
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        <Field
                          as={Select}
                          id="groupAsTicket"
                          name="groupAsTicket"
                          SelectDisplayProps={{ "aria-label": i18n.t("whatsappModal.form.groupAsTicket") }}
                        >
                          <MenuItem value={"disabled"}>{i18n.t("whatsappModal.menuItem.disabled")}</MenuItem>
                          <MenuItem value={"enabled"}>{i18n.t("whatsappModal.menuItem.enabled")}</MenuItem>
                        </Field>
                      </FormControl>
                    </SettingRow>
                  </FormSection>

                  <FormSection title="Mídia da saudação" description="Imagem ou vídeo enviado junto com a mensagem de saudação.">
                    <div className={classes.uploadRow}>
                      <input
                        type="file"
                        accept="video/*,image/*"
                        ref={inputFileRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                      />
                      <Button variant="outlined" onClick={() => inputFileRef.current.click()}>
                        {i18n.t("userModal.buttons.addImage")}
                      </Button>
                      {attachmentName && (
                        <Chip
                          variant="outlined"
                          label={attachmentName}
                          onDelete={handleDeleFile}
                          deleteIcon={<DeleteOutlineIcon aria-label="Remover mídia da saudação" />}
                        />
                      )}
                    </div>
                  </FormSection>

                  <FormSection title="Importação de mensagens">
                    <SettingRow label={i18n.t("whatsappModal.form.importOldMessagesEnable")}>
                      <Switch
                        checked={enableImportMessage}
                        onChange={handleEnableImportMessage}
                        name="importOldMessagesEnable"
                        color="primary"
                        inputProps={{ "aria-label": i18n.t("whatsappModal.form.importOldMessagesEnable") }}
                      />
                    </SettingRow>

                    {enableImportMessage ? (
                      <>
                        <SettingRow label={i18n.t("whatsappModal.form.importOldMessagesGroups")}>
                          <Switch
                            checked={importOldMessagesGroups}
                            onChange={(e) =>
                              setImportOldMessagesGroups(e.target.checked)
                            }
                            name="importOldMessagesGroups"
                            color="primary"
                            inputProps={{ "aria-label": i18n.t("whatsappModal.form.importOldMessagesGroups") }}
                          />
                        </SettingRow>

                        <SettingRow label={i18n.t("whatsappModal.form.closedTicketsPostImported")}>
                          <Switch
                            checked={closedTicketsPostImported}
                            onChange={(e) =>
                              setClosedTicketsPostImported(e.target.checked)
                            }
                            name="closedTicketsPostImported"
                            color="primary"
                            inputProps={{ "aria-label": i18n.t("whatsappModal.form.closedTicketsPostImported") }}
                          />
                        </SettingRow>

                        <div className={clsx(p.grid2, classes.spaced)}>
                          <Field
                            fullWidth
                            as={TextField}
                            label={i18n.t("whatsappModal.form.importOldMessages")}
                            type="datetime-local"
                            name="importOldMessages"
                            id="connection-importOldMessages"
                            inputProps={{
                              max: moment()
                                .add(0, "minutes")
                                .format("YYYY-MM-DDTHH:mm"),
                              min: moment()
                                .add(-2, "years")
                                .format("YYYY-MM-DDTHH:mm"),
                            }}
                            //min="2022-11-06T22:22:55"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            error={
                              touched.importOldMessages &&
                              Boolean(errors.importOldMessages)
                            }
                            helperText={
                              touched.importOldMessages && errors.importOldMessages
                            }
                            variant="outlined"
                            size="small"
                            value={moment(importOldMessages).format(
                              "YYYY-MM-DDTHH:mm"
                            )}
                            onChange={(e) => {
                              setImportOldMessages(e.target.value);
                            }}
                          />
                          <Field
                            fullWidth
                            as={TextField}
                            label={i18n.t("whatsappModal.form.importRecentMessages")}
                            type="datetime-local"
                            name="importRecentMessages"
                            id="connection-importRecentMessages"
                            inputProps={{
                              max: moment()
                                .add(0, "minutes")
                                .format("YYYY-MM-DDTHH:mm"),
                              min: moment(importOldMessages).format(
                                "YYYY-MM-DDTHH:mm"
                              )
                            }}
                            //min="2022-11-06T22:22:55"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            error={
                              touched.importRecentMessages &&
                              Boolean(errors.importRecentMessages)
                            }
                            helperText={
                              touched.importRecentMessages && errors.importRecentMessages
                            }
                            variant="outlined"
                            size="small"
                            value={moment(importRecentMessages).format(
                              "YYYY-MM-DDTHH:mm"
                            )}
                            onChange={(e) => {
                              setImportRecentMessages(e.target.value);
                            }}
                          />
                        </div>
                      </>
                    ) : null}
                    {enableImportMessage && (
                      <div className={classes.importAlert}>
                        {i18n.t("whatsappModal.form.importAlert")}
                      </div>
                    )}
                  </FormSection>

                  {/* TOKEN */}
                  <FormSection title="Chave da API">
                    <div className={classes.inlineRow}>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.token")}
                        type="token"
                        fullWidth
                        // name="token"
                        id="connection-token"
                        value={autoToken}
                        variant="outlined"
                        size="small"
                        disabled
                      />
                      <Tooltip title="Gerar nova chave da API">
                        <span>
                          <IconButton
                            onClick={handleRefreshToken}
                            aria-label="Gerar nova chave da API"
                            disabled={isSubmitting}
                          >
                            <Autorenew fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={copied ? "Chave copiada" : "Copiar chave da API"}>
                        <IconButton
                          onClick={handleCopyToken}
                          aria-label="Copiar chave da API"
                          className={copied ? classes.copied : undefined}
                        >
                          <FileCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </FormSection>

                  <FormSection
                    title={i18n.t("whatsappModal.form.queueRedirection")}
                    description={i18n.t("whatsappModal.form.queueRedirectionDesc")}
                  >
                    <div className={p.grid2}>
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        <InputLabel id="sendIdQueue-selection-label">
                          {i18n.t("whatsappModal.form.sendIdQueue")}
                        </InputLabel>
                        <Field
                          as={Select}
                          name="sendIdQueue"
                          id="sendIdQueue"
                          value={values.sendIdQueue || '0'}
                          label={i18n.t("whatsappModal.form.sendIdQueue")}
                          placeholder={i18n.t("whatsappModal.form.sendIdQueue")}
                          labelId="sendIdQueue-selection-label"
                        >
                          <MenuItem value={0}>&nbsp;</MenuItem>
                          {queues.map(queue => (
                            <MenuItem key={queue.id} value={queue.id}>
                              {queue.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>

                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeSendQueue")}
                        fullWidth
                        name="timeSendQueue"
                        id="connection-timeSendQueue"
                        variant="outlined"
                        size="small"
                        error={touched.timeSendQueue && Boolean(errors.timeSendQueue)}
                        helperText={touched.timeSendQueue && errors.timeSendQueue}
                      />
                    </div>
                  </FormSection>
                </TabPanel>
                <TabPanel
                  value={tab}
                  name={"integrations"}
                >
                  <FormSection title="Filas, integração e assistente">
                    <div className={clsx(p.stack, classes.flush)}>
                      {/* FILAS */}
                      <QueueSelect
                        selectedQueueIds={selectedQueueIds}
                        onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                      />
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        <InputLabel id="integrationId-selection-label">
                          {i18n.t("queueModal.form.integrationId")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("queueModal.form.integrationId")}
                          name="integrationId"
                          id="integrationId"
                          variant="outlined"
                          placeholder={i18n.t("queueModal.form.integrationId")}
                          labelId="integrationId-selection-label"                        >
                          <MenuItem value={null} >{"Desabilitado"}</MenuItem>
                          {integrations.map((integration) => (
                            <MenuItem key={integration.id} value={integration.id}>
                              {integration.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                      <FormControl
                        size="small"
                        variant="outlined"
                        fullWidth
                      >
                        <InputLabel id="dialog-select-prompt-label">
                          {i18n.t("whatsappModal.form.prompt")}
                        </InputLabel>
                        <Select
                          labelId="dialog-select-prompt-label"
                          id="dialog-select-prompt"
                          name="promptId"
                          value={selectedPrompt || ""}
                          onChange={handleChangePrompt}
                          label={i18n.t("whatsappModal.form.prompt")}
                          fullWidth
                          MenuProps={{
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                            getContentAnchorEl: null,
                          }}
                        >
                          {prompts.map((prompt) => (
                            <MenuItem
                              key={prompt.id}
                              value={prompt.id}
                            >
                              {prompt.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </FormSection>
                </TabPanel>


                <TabPanel
                  value={tab}
                  name={"messages"}
                >
                  <FormSection title="Mensagens automáticas">
                    <div className={p.stack}>
                      {/* MENSAGEM DE SAUDAÇÃO */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.greetingMessage")}
                        type="greetingMessage"
                        multiline
                        rows={4}
                        fullWidth
                        name="greetingMessage"
                        id="connection-greetingMessage"
                        error={
                          touched.greetingMessage && Boolean(errors.greetingMessage)
                        }
                        helperText={
                          touched.greetingMessage && errors.greetingMessage
                        }
                        variant="outlined"
                      />

                      {/* MENSAGEM DE CONCLUSÃO */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.complationMessage")}
                        multiline
                        rows={4}
                        fullWidth
                        name="complationMessage"
                        id="connection-complationMessage"
                        error={
                          touched.complationMessage &&
                          Boolean(errors.complationMessage)
                        }
                        helperText={
                          touched.complationMessage && errors.complationMessage
                        }
                        variant="outlined"
                      />

                      {/* MENSAGEM DE FORA DE EXPEDIENTE */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.outOfHoursMessage")}
                        multiline
                        rows={4}
                        fullWidth
                        name="outOfHoursMessage"
                        id="connection-outOfHoursMessage"
                        error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
                        helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
                        variant="outlined"
                      />
                    </div>
                  </FormSection>
                </TabPanel>

                <TabPanel
                  value={tab}
                  name={"chatbot"}
                >
                  <FormSection title="Menu e novos atendimentos">
                    <div className={p.grid3}>
                      {/* TEMPO PARA CRIAR NOVO TICKET */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeCreateNewTicket")}
                        fullWidth
                        name="timeCreateNewTicket"
                        id="connection-timeCreateNewTicket"
                        variant="outlined"
                        size="small"
                        error={touched.timeCreateNewTicket && Boolean(errors.timeCreateNewTicket)}
                        helperText={touched.timeCreateNewTicket && errors.timeCreateNewTicket}
                      />

                      {/* QUANTIDADE MÁXIMA DE VEZES QUE O CHATBOT VAI SER ENVIADO */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.maxUseBotQueues")}
                        fullWidth
                        name="maxUseBotQueues"
                        id="connection-maxUseBotQueues"
                        variant="outlined"
                        size="small"
                        error={touched.maxUseBotQueues && Boolean(errors.maxUseBotQueues)}
                        helperText={touched.maxUseBotQueues && errors.maxUseBotQueues}
                      />
                      {/* TEMPO PARA ENVIO DO CHATBOT */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeUseBotQueues")}
                        fullWidth
                        name="timeUseBotQueues"
                        id="connection-timeUseBotQueues"
                        variant="outlined"
                        size="small"
                        error={touched.timeUseBotQueues && Boolean(errors.timeUseBotQueues)}
                        helperText={touched.timeUseBotQueues && errors.timeUseBotQueues}
                      />
                    </div>
                  </FormSection>

                  <FormSection title="Encerramento automático">
                    <div className={p.grid2}>
                      {/* ENCERRAR CHATS ABERTOS APÓS X HORAS */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresTicket")}
                        fullWidth
                        name="expiresTicket"
                        id="connection-expiresTicket"
                        variant="outlined"
                        size="small"
                        error={touched.expiresTicket && Boolean(errors.expiresTicket)}
                        helperText={touched.expiresTicket && errors.expiresTicket}
                      />
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                      >
                        <InputLabel id="whenExpiresTicket-selection-label">
                          {i18n.t("whatsappModal.form.whenExpiresTicket")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("whatsappModal.form.whenExpiresTicket")}
                          placeholder={i18n.t(
                            "whatsappModal.form.whenExpiresTicket"
                          )}
                          labelId="whenExpiresTicket-selection-label"
                          id="whenExpiresTicket"
                          name="whenExpiresTicket"
                        >
                          <MenuItem value={"0"}>{i18n.t("whatsappModal.form.closeLastMessageOptions1")}</MenuItem>
                          <MenuItem value={"1"}>{i18n.t("whatsappModal.form.closeLastMessageOptions2")}</MenuItem>
                        </Field>
                      </FormControl>
                      {/* MENSAGEM POR INATIVIDADE*/}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresInactiveMessage")}
                        multiline
                        rows={4}
                        fullWidth
                        className={p.span2}
                        name="expiresInactiveMessage"
                        id="connection-expiresInactiveMessage"
                        error={touched.expiresInactiveMessage && Boolean(errors.expiresInactiveMessage)}
                        helperText={touched.expiresInactiveMessage && errors.expiresInactiveMessage}
                        variant="outlined"
                      />
                    </div>
                  </FormSection>

                  <FormSection title="Aviso de inatividade">
                    <div className={p.stack}>
                      {/* TEMPO PARA ENVIO DE MENSAGEM POR INATIVIDADE */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.timeInactiveMessage")}
                        fullWidth
                        name="timeInactiveMessage"
                        id="connection-timeInactiveMessage"
                        variant="outlined"
                        size="small"
                        error={touched.timeInactiveMessage && Boolean(errors.timeInactiveMessage)}
                        helperText={touched.timeInactiveMessage && errors.timeInactiveMessage}
                      />
                      {/* MENSAGEM POR INATIVIDADE*/}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.inactiveMessage")}
                        multiline
                        rows={4}
                        fullWidth
                        name="inactiveMessage"
                        id="connection-inactiveMessage"
                        error={touched.inactiveMessage && Boolean(errors.inactiveMessage)}
                        helperText={touched.inactiveMessage && errors.inactiveMessage}
                        variant="outlined"
                      />
                    </div>
                  </FormSection>
                </TabPanel>
                <TabPanel
                  value={tab}
                  name={"nps"}
                >
                  <FormSection title="Avaliação do atendimento">
                    <div className={p.grid2}>
                      {/* MENSAGEM DE AVALIAÇAO*/}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.ratingMessage")}
                        multiline
                        rows={4}
                        fullWidth
                        className={p.span2}
                        name="ratingMessage"
                        id="connection-ratingMessage"
                        error={touched.ratingMessage && Boolean(errors.ratingMessage)}
                        helperText={touched.ratingMessage && errors.ratingMessage}
                        variant="outlined"
                      />
                      {/* QUANTIDADE MÁXIMA DE VEZES QUE O NPS VAI SER ENVIADO */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.maxUseBotQueuesNPS")}
                        fullWidth
                        name="maxUseBotQueuesNPS"
                        id="connection-maxUseBotQueuesNPS"
                        variant="outlined"
                        size="small"
                        error={touched.maxUseBotQueuesNPS && Boolean(errors.maxUseBotQueuesNPS)}
                        helperText={touched.maxUseBotQueuesNPS && errors.maxUseBotQueuesNPS}
                      />
                      {/* ENCERRAR CHATS NPS APÓS X Minutos */}
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresTicketNPS")}
                        fullWidth
                        name="expiresTicketNPS"
                        id="connection-expiresTicketNPS"
                        variant="outlined"
                        size="small"
                        error={touched.expiresTicketNPS && Boolean(errors.expiresTicketNPS)}
                        helperText={touched.expiresTicketNPS && errors.expiresTicketNPS}
                      />
                    </div>
                  </FormSection>
                </TabPanel>
                <TabPanel
                  value={tab}
                  name={"schedules"}
                >
                  {tab === "schedules" && (
                    <SchedulesForm
                      loading={false}
                      onSubmit={handleSaveSchedules}
                      initialValues={schedules}
                      labelSaveButton={i18n.t("whatsappModal.buttons.okAdd")}
                    />
                  )}

                </TabPanel>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
